import { useNavigate } from 'react-router';
import { useLatestRef } from '@gonfalon/react/useLatestRef';

import { CommandPaletteExecutionError } from '../command-palette-execution-error';
import {
  useInternalCommandPaletteActions,
  useInternalCommandPaletteState,
} from '../components/command-palette-state-provider';
import { useCommandPaletteTelemetry } from '../components/command-palette-telemetry-context';
import { useCommandRegistry } from '../components/command-registry-provider';

export interface UseCommandExecutionResult {
  executeCommandById: (
    commandId: string | number,
    options: {
      executionType: 'action-execution' | 'keyboard-shortcut';
      message: string;
    },
  ) => Promise<void>;
}

/**
 * Hook for activating commands by id.
 *
 * Switches on command type:
 *   - `view`: dispatch `openView` (no prefill -- views activated from
 *     the listbox or keyboard don't carry a payload; schema-typed
 *     prefill is only for programmatic `openView(cmd, prefill)`
 *     callers).
 *   - `link`: navigate or open new tab, then close the palette.
 *   - `action`: dispatched by the presence of `run`. An action with
 *     a `run` callback is a leaf -- execute and close. An action
 *     WITHOUT a `run` callback is a scope -- enter it as a root.
 *     Children may register lazily once `rootCommandId` matches
 *     (the production search-flag pattern); the activation rule
 *     intentionally does not consult `hasChildren` so a scope that
 *     hasn't yet loaded its children still opens correctly. The
 *     scope's `emptyState` field covers the no-children-yet UX.
 *
 * `command-executed` analytics emit here -- it's a synchronous "user
 * did X" event, not a mode diff. The provider's mode-diff effect
 * handles every other event.
 */
export function useCommandExecution(): UseCommandExecutionResult {
  const navigate = useNavigate();
  const { commands } = useCommandRegistry();
  const { onEvent, onError } = useCommandPaletteTelemetry();
  const actions = useInternalCommandPaletteActions();
  const state = useInternalCommandPaletteState();

  const onEventRef = useLatestRef(onEvent);
  const onErrorRef = useLatestRef(onError);

  const executeCommandById = async (
    commandId: string | number,
    {
      executionType,
      message,
    }: {
      executionType: 'action-execution' | 'keyboard-shortcut';
      message: string;
    },
  ) => {
    const command = commands[commandId as string];
    if (!command) {
      return;
    }

    try {
      switch (command.type) {
        case 'view': {
          actions.openView(command);
          break;
        }

        case 'link': {
          if (command.newTab) {
            window.open(command.href as string, '_blank');
          } else {
            void navigate(command.href, command.routerOptions);
          }
          onEventRef.current({
            type: 'command-executed',
            command: { id: command.id, type: command.type, label: command.label },
            timestamp: Date.now(),
          });
          actions.close();
          break;
        }

        case 'action': {
          // An action without a `run` callback is a scope: activating
          // it enters the command as a root rather than executing.
          // We rely on the declared shape (no `run`) instead of
          // checking for children at runtime, because children can be
          // registered lazily after the user enters the scope.
          if (!command.run) {
            actions.openRoot(command.id);
            return;
          }
          await command.run();
          onEventRef.current({
            type: 'command-executed',
            command: { id: command.id, type: command.type, label: command.label },
            timestamp: Date.now(),
          });
          actions.close();
          break;
        }

        // `command.type` is a closed union; the cases above are
        // exhaustive. This branch only exists to satisfy oxlint's
        // default-case rule.
        default:
          break;
      }
    } catch (e) {
      const liveQuery = state.mode.type === 'open' ? state.mode.query : '';
      const liveRoot = state.mode.type === 'open' ? (state.mode.rootCommandId ?? undefined) : undefined;
      const commandPaletteError = new CommandPaletteExecutionError({
        command: { id: command.id, type: command.type },
        executionType,
        message,
        cause: e,
        context: {
          query: liveQuery,
          activeScope: liveRoot,
          resultCount: Object.keys(commands).length,
        },
      });
      onErrorRef.current(commandPaletteError);
    }
  };

  return { executeCommandById };
}
